import React, { useState, useRef, useEffect } from 'react';
import useClickOutside from '../../../hooks/useClickOutside';
import { getAssessMentQuestionnairesByRoleAndCandidate } from '../../../api/assessmentquestionnaires.api';
import { GradeCandidateInterview } from '../../../api/candidates.api';
import { useInterviewSocket } from '../../../hooks/useInterviewSocket';
import useApiToken from '../../../hooks/useApiToken';
import { useNotification } from '../../../context/NotificationContext';

interface Question {
  id: string;
  text: string;
  type: string;
  time: number;
  candidateAnswers: CandidateAnswer[] | null;
  parentQuestionId?: string | null;
}

interface QuestionMap {
  id: string;
  questionnaireId: string;
  questionId: string;
  question: Question;
}

interface CandidateAnswer {
  id: string;
  candidateId: string;
  accuracy: number | null;
  manualEvaluation: string | null;
  answer: Answer;
}

interface Answer {
  id: string;
  text: string;
}

interface InterviewControlPanelProps {
  onClose?: () => void;
  candidateId: string;
  roleId: string;
  interviewId: string | null;
}

// Helper function to strip HTML tags from text and decode HTML entities
const stripHtml = (html: string): string => {
  if (!html) return '';
  // Use a temporary DOM element to decode HTML entities properly
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Interface for recursive tree node (infinite nesting)
interface QuestionTreeNode {
  question: QuestionMap;
  depth: number;
  children: QuestionTreeNode[];
}

// Build recursive tree from flat list of questions
const buildQuestionTree = (questions: QuestionMap[]): QuestionTreeNode[] => {
  // Find root questions (no parent or parent not in list)
  const questionIds = new Set(questions.map(q => q.questionId));
  const roots = questions.filter(q =>
    !q.question.parentQuestionId ||
    !questionIds.has(q.question.parentQuestionId)
  );

  const buildNode = (q: QuestionMap, depth: number): QuestionTreeNode => {
    const children = questions.filter(
      child => child.question.parentQuestionId === q.questionId
    );
    return {
      question: q,
      depth,
      children: children.map(c => buildNode(c, depth + 1))
    };
  };

  return roots.map(r => buildNode(r, 0));
};

// Flatten tree back to array (depth-first order)
const flattenTree = (nodes: QuestionTreeNode[]): QuestionMap[] => {
  const result: QuestionMap[] = [];
  const flatten = (node: QuestionTreeNode) => {
    result.push(node.question);
    node.children.forEach(flatten);
  };
  nodes.forEach(flatten);
  return result;
};

// Get all descendants of a question (for drag/remove operations)
const getAllDescendants = (questionId: string, questions: QuestionMap[]): QuestionMap[] => {
  const descendants: QuestionMap[] = [];
  const findDescendants = (parentId: string) => {
    const children = questions.filter(q => q.question.parentQuestionId === parentId);
    children.forEach(child => {
      descendants.push(child);
      findDescendants(child.questionId);
    });
  };
  findDescendants(questionId);
  return descendants;
};

// Get ancestor chain (for displaying context)
const getAncestorChain = (questionId: string, questions: QuestionMap[]): QuestionMap[] => {
  const chain: QuestionMap[] = [];
  let currentId: string | null | undefined = questionId;

  while (currentId) {
    const question = questions.find(q => q.questionId === currentId);
    if (!question?.question.parentQuestionId) break;

    const parent = questions.find(q => q.questionId === question.question.parentQuestionId);
    if (parent) {
      chain.unshift(parent);
      currentId = parent.questionId;
    } else break;
  }

  return chain;
};

// Calculate depth of a question
const getQuestionDepth = (questionId: string, questions: QuestionMap[]): number => {
  let depth = 0;
  let currentId: string | null | undefined = questionId;

  while (currentId) {
    const question = questions.find(q => q.questionId === currentId);
    if (!question?.question.parentQuestionId) break;
    currentId = question.question.parentQuestionId;
    depth++;
  }

  return depth;
};

// Recursive component for Assessment Questions panel
const AssessmentQuestionNode: React.FC<{
  node: QuestionTreeNode;
  selectedQuestions: Set<string>;
  handleToggleQuestion: (id: string) => void;
  getParentTextById: (id: string) => string;
}> = ({ node, selectedQuestions, handleToggleQuestion, getParentTextById }) => {
  const { question: qm, depth, children } = node;
  const isAnswered = qm?.question?.candidateAnswers && qm.question.candidateAnswers.length > 0;
  const hasChildren = children.length > 0;
  // Check if this is an orphaned follow-up (has parent but shows as root because parent is in queue)
  const isOrphanedFollowUp = depth === 0 && !!qm.question.parentQuestionId;
  const parentText = isOrphanedFollowUp ? getParentTextById(qm.question.parentQuestionId!) : null;

  return (
    <div
      className={depth > 0 ? "border-l-2 border-blue-200 dark:border-blue-800" : "border-l-2 border-gray-200 dark:border-gray-600"}
      style={{ marginLeft: depth > 0 ? `${Math.min(depth * 12, 48)}px` : '4px' }}
    >
      <label className={`flex items-start p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition ${depth > 0 ? 'pl-3 hover:bg-blue-50' : ''}`}>
        <input
          type="checkbox"
          checked={selectedQuestions.has(qm.id)}
          onChange={() => {
            if (isAnswered) return;
            handleToggleQuestion(qm.id);
          }}
          className="h-4 w-4 mt-0.5"
        />
        <div className="ml-2 flex-1">
          <div className="flex items-start gap-1">
            {depth > 0 && <span className="text-blue-500 text-xs flex-shrink-0">{'↳'.repeat(Math.min(depth, 3))}</span>}
            <div
              className={`text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-xs [&_pre]:overflow-x-auto [&_code]:text-xs ${isAnswered ? "line-through opacity-50" : ""}`}
              dangerouslySetInnerHTML={{ __html: qm.question.text || '' }}
            />
          </div>
          {/* Show parent context for orphaned follow-ups */}
          {isOrphanedFollowUp && parentText && (
            <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
              ↳ follow-up of "{parentText.substring(0, 30)}{parentText.length > 30 ? '...' : ''}"
            </div>
          )}
          {hasChildren && (
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 font-medium">
                {children.length} follow-up{children.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </label>
      {/* Recursively render children */}
      {children.map((child) => (
        <AssessmentQuestionNode
          key={child.question.id}
          node={child}
          selectedQuestions={selectedQuestions}
          handleToggleQuestion={handleToggleQuestion}
          getParentTextById={getParentTextById}
        />
      ))}
    </div>
  );
};

// Recursive component for Interview Queue panel
const InterviewQueueNode: React.FC<{
  node: QuestionTreeNode;
  interviewQueue: QuestionMap[];
  activeQuestionIndex: number;
  draggedIndex: number | null;
  isUnattempted: (index: number) => boolean;
  isRootInQueue: (qm: QuestionMap) => boolean;
  handleDragStart: (e: React.DragEvent<HTMLDivElement>, index: number, parentId: string | null | undefined) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>, targetIndex: number, targetParentId: string | null | undefined) => void;
  handleDragEnd: () => void;
  handleRemoveFromQueue: (index: number) => void;
  getStatusBadge: (index: number) => JSX.Element;
  getParentTextById: (id: string) => string;
  questionNumber: { current: number };
  parentNumberPrefix: string;
}> = ({
  node,
  interviewQueue,
  activeQuestionIndex,
  draggedIndex,
  isUnattempted,
  isRootInQueue,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  handleRemoveFromQueue,
  getStatusBadge,
  getParentTextById,
  questionNumber,
  parentNumberPrefix,
}) => {
  const { question: qm, depth, children } = node;
  const index = interviewQueue.findIndex(q => q.id === qm.id);
  const isActive = index === activeQuestionIndex;
  const isDragged = draggedIndex === index;
  // Allow dragging for both root questions and follow-ups (if unattempted)
  const canDrag = isUnattempted(index);
  const hasChildren = children.length > 0;
  const parentId = qm.question.parentQuestionId;

  // Increment question number for display
  const currentNumber = parentNumberPrefix || String(++questionNumber.current);

  return (
    <div
      className={`${depth === 0 ? 'border-l-4 border-gray-300 dark:border-gray-600 rounded-r-md overflow-hidden' : ''}`}
      draggable={canDrag}
      onDragStart={(e) => {
        e.stopPropagation();
        if (canDrag) handleDragStart(e, index, parentId);
      }}
      onDragOver={(e) => {
        e.stopPropagation();
        handleDragOver(e);
      }}
      onDrop={(e) => {
        e.stopPropagation();
        handleDrop(e, index, parentId);
      }}
      onDragEnd={(e) => {
        e.stopPropagation();
        handleDragEnd();
      }}
    >
      <div
        className={`flex items-start justify-between p-3 transition ${
          depth > 0 ? `pl-${Math.min(4 + depth * 4, 12)} border-t border-gray-200 dark:border-gray-700` : ''
        } ${
          isActive
            ? 'bg-blue-100 dark:bg-blue-900 border-l-4 border-l-entntblue -ml-1'
            : isDragged
            ? 'bg-orange-50 dark:bg-gray-700 opacity-50'
            : depth > 0
            ? 'bg-white dark:bg-gray-900'
            : 'bg-gray-50 dark:bg-gray-800'
        } ${canDrag ? 'cursor-move' : 'cursor-default'}`}
        style={{ paddingLeft: depth > 0 ? `${16 + Math.min(depth * 16, 48)}px` : undefined }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            {canDrag && <span className="text-gray-400 dark:text-gray-500 mt-0.5 cursor-move">≡</span>}
            {depth > 0 && <span className="text-blue-500 dark:text-blue-400 text-sm">{'↳'.repeat(Math.min(depth, 3))}</span>}
            <div className="flex-1">
              <div className="flex items-start gap-1">
                <span className={`${depth === 0 ? 'font-medium' : ''} text-gray-800 dark:text-white text-sm flex-shrink-0`}>
                  {currentNumber}.
                </span>
                <div
                  className={`${depth === 0 ? 'font-medium' : ''} text-gray-800 dark:text-white text-sm prose prose-sm dark:prose-invert max-w-none [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-xs [&_pre]:overflow-x-auto [&_code]:text-xs`}
                  dangerouslySetInnerHTML={{ __html: qm.question.text || '' }}
                />
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {getStatusBadge(index)}
                {hasChildren && (
                  <span className="px-1.5 py-0.5 text-[10px] rounded bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300 font-medium">
                    {children.length} follow-up{children.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={() => handleRemoveFromQueue(index)}
          className={`ml-2 flex-shrink-0 ${depth === 0 ? 'text-red-500 hover:text-red-700 font-bold text-lg' : 'text-red-400 hover:text-red-600 text-sm'} dark:hover:text-red-400 transition`}
          title={hasChildren ? "Remove question and all follow-ups" : "Remove from queue"}
        >
          ✕
        </button>
      </div>
      {/* Recursively render children */}
      {children.map((child, idx) => (
        <InterviewQueueNode
          key={child.question.id}
          node={child}
          interviewQueue={interviewQueue}
          activeQuestionIndex={activeQuestionIndex}
          draggedIndex={draggedIndex}
          isUnattempted={isUnattempted}
          isRootInQueue={isRootInQueue}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          handleDragEnd={handleDragEnd}
          handleRemoveFromQueue={handleRemoveFromQueue}
          getStatusBadge={getStatusBadge}
          getParentTextById={getParentTextById}
          questionNumber={questionNumber}
          parentNumberPrefix={`${currentNumber}.${idx + 1}`}
        />
      ))}
    </div>
  );
};

const InterviewControlPanel: React.FC<InterviewControlPanelProps> = ({ onClose, candidateId, roleId, interviewId }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [questionMaps, setQuestionMaps] = useState<QuestionMap[]>([]);
  const [interviewQueue, setInterviewQueue] = useState<QuestionMap[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);
  const [candidateAnswer, setCandidateAnswer] = useState<CandidateAnswer>({
    id: 'ans1',
    candidateId: 'cand1',
    accuracy: null,
    manualEvaluation: null,
    answer: { id: 'a1', text: 'HTTP methods are the different request types in HTTP protocol like GET, POST, PUT, DELETE...' },
  });
  const [selectedGrade, setSelectedGrade] = useState<'wrong' | 'partial' | 'correct' | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedParentId, setDraggedParentId] = useState<string | null | undefined>(null);
  const [socketError, setSocketError] = useState<string | null>(null);

  const token = useApiToken();
  const { info, success } = useNotification();

  // Initialize socket connection
  const connectionRef = useInterviewSocket(
    interviewId,
    "interviewer",
    (q) => {
      console.log("Question received from socket:", q);
      // Handle incoming messages from socket if needed
    }
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const fetchQuestions = async () => {
    if (!token) return;
    try {
      const res = await getAssessMentQuestionnairesByRoleAndCandidate(candidateId, roleId, interviewId || undefined, token);
      console.log("Fetched Questions: ", res);
      setQuestionMaps(res?.assessmentQuestionMappings);
    } catch (error) {
      console.log(error)
    }
  }

  const handleToggleQuestion = (questionId: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  const handleAddToQueue = () => {
    if (selectedQuestions.size === 0) {
      info('No Questions Selected', 'Please select at least one question to add');
      return;
    }

    const questionsToAdd = questionMaps.filter((qm) => selectedQuestions.has(qm.id));

    // Sort questions: root questions first, then follow-ups by their parent
    const rootQuestions = questionsToAdd.filter(q => !q.question.parentQuestionId);
    const followUpQuestions = questionsToAdd.filter(q => !!q.question.parentQuestionId);

    let newQueue = [...interviewQueue];

    // Add root questions to the end
    newQueue = [...newQueue, ...rootQuestions];

    // For each follow-up, insert it after its parent (or parent's last descendant)
    for (const followUp of followUpQuestions) {
      const parentId = followUp.question.parentQuestionId;

      // Find the parent's position in the queue
      const parentIndex = newQueue.findIndex(q => q.questionId === parentId);

      if (parentIndex !== -1) {
        // Find the last descendant of the parent to insert after
        const descendants = getAllDescendants(parentId!, newQueue);
        const lastDescendantIndex = descendants.length > 0
          ? Math.max(...descendants.map(d => newQueue.findIndex(q => q.id === d.id)))
          : parentIndex;

        // Insert after the parent or its last descendant
        newQueue.splice(lastDescendantIndex + 1, 0, followUp);
      } else {
        // Parent not in queue, add to the end
        newQueue.push(followUp);
      }
    }

    setInterviewQueue(newQueue);
    setSelectedQuestions(new Set());
  };

  const handleRemoveFromQueue = (indexToRemove: number) => {
    const questionToRemove = interviewQueue[indexToRemove];

    // Get all descendants (recursive) to remove along with this question
    const descendants = getAllDescendants(questionToRemove.questionId, interviewQueue);
    const idsToRemove = new Set([questionToRemove.id, ...descendants.map(d => d.id)]);

    const newQueue = interviewQueue.filter(q => !idsToRemove.has(q.id));
    setInterviewQueue(newQueue);

    // Adjust active question index if needed
    if (activeQuestionIndex >= newQueue.length) {
      setActiveQuestionIndex(Math.max(-1, newQueue.length - 1));
    }
  };


  const handlePushNextQuestion = async () => {
    if (activeQuestionIndex < interviewQueue.length - 1) {
      const nextIndex = activeQuestionIndex + 1;
      const nextQuestion = interviewQueue[nextIndex];

      const isFollowUp = !!nextQuestion.question?.parentQuestionId;
      const depth = getQuestionDepth(nextQuestion.questionId, questionMaps);

      // Get full ancestor chain for context (infinite nesting support)
      const ancestorChain = getAncestorChain(nextQuestion.questionId, questionMaps);
      const ancestorTexts = ancestorChain.map(a => a.question.text);

      try {
        // Send question to candidate via socket with full ancestor chain
        await connectionRef.current?.invoke(
          "PushQuestion",
          {
            interviewId: interviewId,
            questionText: nextQuestion.question?.text,
            isFollowUp: isFollowUp,
            ancestorChain: ancestorTexts, // Full chain of parent texts
            depth: depth,
            questionNumber: nextIndex + 1 // Send the actual question number
          }
        );

        setActiveQuestionIndex(nextIndex);
        setCandidateAnswer({
          id: 'ans' + (nextIndex + 1),
          candidateId: 'cand1',
          accuracy: null,
          manualEvaluation: null,
          answer: { id: 'a' + (nextIndex + 1), text: '' },
        });
        setSelectedGrade(null);
        setSocketError(null);
      } catch (error) {
        console.error("Error pushing question:", error);
        setSocketError("Failed to push question. Please try again.");
      }
    }
  };

  const handleSaveGrade = async () => {
    if (selectedGrade && activeQuestionIndex !== -1 && token && interviewId) {
      const gradeValue = selectedGrade === 'correct' ? 2 : selectedGrade === 'partial' ? 1 : 0;
      const currentQuestion = interviewQueue[activeQuestionIndex];
      
      try {
        await GradeCandidateInterview(
          interviewId,
          currentQuestion.questionId,
          gradeValue,
          token
        );
        
        const updatedAnswer: CandidateAnswer = {
          ...candidateAnswer,
          accuracy: gradeValue,
          manualEvaluation: selectedGrade,
        };
        setCandidateAnswer(updatedAnswer);
        setSelectedGrade(null);
        success('Grade Saved', `Question graded as "${selectedGrade}"`);
      } catch (error) {
        console.error("Error saving grade:", error);
        setSocketError("Failed to save grade. Please try again.");
      }
    }
  };

  const getParentQuestionText = (parentId: string): string | null => {
    const parent = questionMaps.find(q => q.questionId === parentId);
    return parent?.question?.text || null;
  };

  const filteredQuestions = questionMaps.filter((qm) => {
    const matchesSearch = qm.question.text.toLowerCase().includes(searchTerm.toLowerCase());
    const isInQueue = interviewQueue.some(queued => queued.id === qm.id);
    return matchesSearch && !isInQueue;
  });

  // Build recursive tree for filtered questions (Assessment Questions panel)
  const filteredQuestionsTree = buildQuestionTree(filteredQuestions);

  // Build recursive tree for interview queue
  const interviewQueueTree = buildQuestionTree(interviewQueue);

  // Get parent question text by ID
  const getParentTextById = (parentId: string): string => {
    const parent = questionMaps.find(q => q.questionId === parentId);
    return parent ? stripHtml(parent.question.text) : 'Unknown parent';
  };

  // Check if question has any descendants in the queue
  const hasDescendantsInQueue = (questionId: string): number => {
    return getAllDescendants(questionId, interviewQueue).length;
  };

  const getStatusBadge = (index: number) => {
    if (index === activeQuestionIndex) {
      return <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-600 font-semibold">ACTIVE</span>;
    } else if (index < activeQuestionIndex) {
      return <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-600 font-semibold">ANSWERED</span>;
    } else {
      return <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-600 font-semibold">PENDING</span>;
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [token])

  const isUnattempted = (index: number) => index > activeQuestionIndex;

  // Check if a question is a root (no parent in the queue)
  const isRootInQueue = (qm: QuestionMap): boolean => {
    if (!qm.question.parentQuestionId) return true;
    // Check if parent exists in queue
    return !interviewQueue.some(q => q.questionId === qm.question.parentQuestionId);
  };

  // Get all indices of a question and its descendants (recursive) for drag operations
  const getTreeIndices = (index: number): number[] => {
    const question = interviewQueue[index];
    if (!question) return [index];

    const indices: number[] = [index];
    const descendants = getAllDescendants(question.questionId, interviewQueue);
    descendants.forEach(d => {
      const idx = interviewQueue.findIndex(q => q.id === d.id);
      if (idx !== -1) indices.push(idx);
    });

    return indices.sort((a, b) => a - b);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number, parentId: string | null | undefined) => {
    // Allow dragging for all unattempted questions
    if (!isUnattempted(index)) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    setDraggedParentId(parentId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number, targetParentId: string | null | undefined) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDraggedParentId(null);
      return;
    }

    const draggedQuestion = interviewQueue[draggedIndex];

    if (!isUnattempted(draggedIndex)) {
      setDraggedIndex(null);
      setDraggedParentId(null);
      return;
    }

    // Check if dragging a follow-up question
    const isFollowUpDrag = !!draggedParentId;

    if (isFollowUpDrag) {
      // Follow-ups can only be reordered within their parent's children
      if (targetParentId !== draggedParentId) {
        // Can't drop outside of the same parent
        setDraggedIndex(null);
        setDraggedParentId(null);
        return;
      }

      // Get siblings (children of the same parent)
      const siblings = interviewQueue.filter(q => q.question.parentQuestionId === draggedParentId);
      const draggedSiblingIndex = siblings.findIndex(q => q.id === draggedQuestion.id);
      const targetSibling = interviewQueue[targetIndex];
      const targetSiblingIndex = siblings.findIndex(q => q.id === targetSibling?.id);

      if (targetSiblingIndex === -1 || draggedSiblingIndex === -1) {
        setDraggedIndex(null);
        setDraggedParentId(null);
        return;
      }

      // Reorder within siblings
      const newSiblings = [...siblings];
      const [removed] = newSiblings.splice(draggedSiblingIndex, 1);
      newSiblings.splice(targetSiblingIndex, 0, removed);

      // Rebuild the queue with new sibling order
      const newQueue: QuestionMap[] = [];
      for (const q of interviewQueue) {
        if (q.question.parentQuestionId === draggedParentId) {
          // Skip - we'll add reordered siblings after parent
          continue;
        }
        newQueue.push(q);
        // After adding the parent, add all reordered children
        if (q.questionId === draggedParentId) {
          newQueue.push(...newSiblings);
        }
      }

      setInterviewQueue(newQueue);
    } else {
      // Root question drag - existing logic
      // Get the entire tree being dragged (question + all descendants)
      const treeIndices = getTreeIndices(draggedIndex);
      const treeItems = treeIndices.map(i => interviewQueue[i]);

      // Remove the tree from current position
      let newQueue = interviewQueue.filter((_, i) => !treeIndices.includes(i));

      // Calculate new target index after removal
      let newTargetIndex = targetIndex;
      const removedBefore = treeIndices.filter(i => i < targetIndex).length;
      newTargetIndex -= removedBefore;

      // Ensure we don't insert in the middle of another tree
      // Find the next root position
      if (newTargetIndex > 0 && newTargetIndex < newQueue.length) {
        const targetQ = newQueue[newTargetIndex];
        if (targetQ && !isRootInQueue(targetQ)) {
          // Find the end of this tree
          let endIdx = newTargetIndex;
          while (endIdx < newQueue.length && !isRootInQueue(newQueue[endIdx])) {
            endIdx++;
          }
          newTargetIndex = endIdx;
        }
      }

      // Insert the tree at new position
      newQueue.splice(newTargetIndex, 0, ...treeItems);
      setInterviewQueue(newQueue);
    }

    setDraggedIndex(null);
    setDraggedParentId(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDraggedParentId(null);
  };

  return (
    <div 
      onClick={(e) => {
        // Close modal only when clicking on the backdrop, not on the modal content
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }} 
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-hidden"
    >
      <div ref={modalRef} className="bg-white dark:bg-darkbg rounded-lg shadow-lg w-full max-w-7xl max-h-[90vh] overflow-hidden border dark:border-gray-700">
        {/* Header */}
        <div className="bg-entntblue dark:bg-entntdarkblue text-white px-6 py-4 border-b dark:border-gray-700">
          <h1 className="text-2xl font-bold">Interview Control Panel</h1>
        </div>

        {/* Three Column Layout */}
        <div className="flex h-[calc(90vh-64px)] overflow-hidden divide-x dark:divide-gray-700">
          {/* Left Column: Question Bank */}
          <div className="w-1/4 flex flex-col p-6 border-r dark:border-gray-700 bg-gray-50 dark:bg-darkbglight">
            <h2 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">Assessment Questions</h2>

            {/* Search */}
            <div className="mb-4 relative">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-entntblue dark:focus:ring-entntblue"
              />
            </div>

            {/* Question List - Scrollable (Recursive Tree Structure) */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-1">
              {filteredQuestionsTree.map((node) => (
                <AssessmentQuestionNode
                  key={node.question.id}
                  node={node}
                  selectedQuestions={selectedQuestions}
                  handleToggleQuestion={handleToggleQuestion}
                  getParentTextById={getParentTextById}
                />
              ))}
            </div>

            {/* Add Button - Fixed at bottom */}
            <button className="w-full flex items-center justify-center gap-2 bg-entntblue hover:bg-blue-700 dark:bg-entntblue dark:hover:bg-blue-700 text-white py-2 rounded-md font-medium transition flex-shrink-0" onClick={handleAddToQueue}>
              <span className="text-lg">+</span>
              Add
            </button>
          </div>

          {/* Middle Column: Interview Queue */}
          <div className="w-1/3 overflow-y-auto p-6 border-r dark:border-gray-700 bg-white dark:bg-darkbg flex flex-col">
            <h2 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">INTERVIEW QUEUE</h2>

            {/* Queue List (Recursive Tree Display) */}
            <div className="flex-1 overflow-y-auto mb-4 pr-2">
              <div className="space-y-3">
                {(() => {
                  const questionNumber = { current: 0 };
                  return interviewQueueTree.map((node) => (
                    <InterviewQueueNode
                      key={node.question.id}
                      node={node}
                      interviewQueue={interviewQueue}
                      activeQuestionIndex={activeQuestionIndex}
                      draggedIndex={draggedIndex}
                      isUnattempted={isUnattempted}
                      isRootInQueue={isRootInQueue}
                      handleDragStart={handleDragStart}
                      handleDragOver={handleDragOver}
                      handleDrop={handleDrop}
                      handleDragEnd={handleDragEnd}
                      handleRemoveFromQueue={handleRemoveFromQueue}
                      getStatusBadge={getStatusBadge}
                      getParentTextById={getParentTextById}
                      questionNumber={questionNumber}
                      parentNumberPrefix=""
                    />
                  ));
                })()}
              </div>
              {interviewQueue.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400 mt-4">No questions in queue</p>
              )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Move to next question</p>

            {/* Push Next Button */}
            <button
              onClick={handlePushNextQuestion}
              disabled={interviewQueue.length === 0}
              className="w-full bg-entntblue hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 dark:bg-entntblue dark:hover:bg-blue-700 text-white py-2 rounded-md font-medium transition"
            >
              ▶ {activeQuestionIndex === -1 ? 'Start Interview' : 'Push Next Question'}
            </button>
          </div>

          {/* Right Column: Active / Grading */}
          <div className="w-1/3 overflow-y-auto p-6 bg-white dark:bg-darkbg flex flex-col">
            <h2 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">ACTIVE / GRADING</h2>

            {/* Socket Error Alert */}
            {socketError && (
              <div className="mb-4 p-3 rounded-md border border-red-400 bg-red-50 dark:bg-red-900 text-red-700 dark:text-red-200 text-sm">
                {socketError}
              </div>
            )}

            {/* Active Question */}
            <div className="mb-6">
              {activeQuestionIndex === -1 ? (
                <div className="p-4 rounded-md border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                  <p className="text-center text-gray-600 dark:text-gray-400">
                    No question active. Click "Start Interview" to begin.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Question #{activeQuestionIndex + 1} <span className="text-red-500 font-bold">(LIVE)</span>
                    </p>
                    {interviewQueue[activeQuestionIndex]?.question.parentQuestionId && (
                      <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300 font-medium">
                        FOLLOW-UP
                      </span>
                    )}
                  </div>
                  <div
                    className="font-semibold text-gray-800 dark:text-white prose prose-sm dark:prose-invert max-w-none max-h-32 overflow-y-auto [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto [&_code]:text-xs"
                    dangerouslySetInnerHTML={{ __html: interviewQueue[activeQuestionIndex]?.question.text || '' }}
                  />
                </>
              )}
            </div>

            {/* Grading Options */}
            {activeQuestionIndex !== -1 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Grade Answer:</p>
                <div className="space-y-2">
                  <label className="flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition border border-transparent hover:border-red-200">
                    <input
                      type="radio"
                      name="grade"
                      value="wrong"
                      checked={selectedGrade === 'wrong'}
                      onChange={(e) => setSelectedGrade(e.target.value as any)}
                      className="h-4 w-4 text-red-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">Wrong</span>
                  </label>
                  <label className="flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition border border-transparent hover:border-yellow-200">
                    <input
                      type="radio"
                      name="grade"
                      value="partial"
                      checked={selectedGrade === 'partial'}
                      onChange={(e) => setSelectedGrade(e.target.value as any)}
                      className="h-4 w-4 text-yellow-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">Partially Correct</span>
                  </label>
                  <label className="flex items-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition border border-transparent hover:border-green-200">
                    <input
                      type="radio"
                      name="grade"
                      value="correct"
                      checked={selectedGrade === 'correct'}
                      onChange={(e) => setSelectedGrade(e.target.value as any)}
                      className="h-4 w-4 text-green-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">Correct</span>
                  </label>
                </div>
              </div>
            )}

            {/* Save Grade Button */}
            <button
              onClick={handleSaveGrade}
              disabled={!selectedGrade}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white py-2 rounded-md font-medium transition mt-auto"
            >
              Save Grade
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewControlPanel;
